import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FileEntry {
  id: number;
  agency_id: number;
  category: string | null;
  file_key: string;
  file_name: string;
  file_size: number | null;
  uploaded_by: number | null;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FileLibrary() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formCategory, setFormCategory] = useState("");
  const [formFileName, setFormFileName] = useState("");
  const [formFileKey, setFormFileKey] = useState("");

  const { data: files, isLoading } = useQuery<FileEntry[]>({
    queryKey: ["file-library"],
    queryFn: async () => {
      const res = await api.get<{ data: FileEntry[] }>("/file-library");
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post("/file-library", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["file-library"] });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/file-library/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["file-library"] });
    },
  });

  function resetForm() {
    setShowForm(false);
    setFormCategory("");
    setFormFileName("");
    setFormFileKey("");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createMutation.mutate({
      category: formCategory || undefined,
      fileName: formFileName,
      fileKey: formFileKey || `files/${Date.now()}_${formFileName}`,
    });
  }

  function formatFileSize(bytes: number | null) {
    if (bytes == null) return "--";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // Group files by category
  const grouped = new Map<string, FileEntry[]>();
  for (const f of files ?? []) {
    const key = f.category ?? "Uncategorized";
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(f);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            File Library
          </h2>
          <p className="text-sm text-muted-foreground">
            Agency documents and resources
          </p>
        </div>
        <Button
          size="sm"
          className="gap-2"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <Plus className="h-4 w-4" />
          Add File Entry
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">New File Entry</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Input
                  label="File Name"
                  value={formFileName}
                  onChange={(e) => setFormFileName(e.target.value)}
                  required
                  placeholder="Document name"
                />
                <Input
                  label="Category"
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value)}
                  placeholder="e.g. Forms, Policies"
                />
                <Input
                  label="File Key (R2 path)"
                  value={formFileKey}
                  onChange={(e) => setFormFileKey(e.target.value)}
                  placeholder="Auto-generated if empty"
                />
              </div>

              {createMutation.isError && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {(createMutation.error as Error)?.message ??
                    "An error occurred"}
                </div>
              )}

              <div className="flex gap-3">
                <Button type="submit" loading={createMutation.isPending}>
                  Add Entry
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      )}

      {!isLoading && (files ?? []).length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No files in the library yet.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading &&
        Array.from(grouped.entries()).map(([category, categoryFiles]) => (
          <Card key={category}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                {category}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                      File Name
                    </th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                      Size
                    </th>
                    <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                      Uploaded
                    </th>
                    <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {categoryFiles.map((file) => (
                    <tr
                      key={file.id}
                      className="border-b last:border-0 hover:bg-muted/30"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{file.file_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {formatFileSize(file.file_size)}
                      </td>
                      <td className="px-4 py-3">
                        {new Date(file.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (confirm("Delete this file entry?")) {
                              deleteMutation.mutate(file.id);
                            }
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))}
    </div>
  );
}
